import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaChevronLeft, FaChevronRight, FaTrash, FaDownload, FaPlay, FaVideo, FaCalendarAlt, FaEdit, FaSave, FaImage } from 'react-icons/fa';
import { toast } from 'sonner';

const BACKEND_URL = '';

const isVideo = (photo) => photo.media_type === 'video' || (photo.content_type && photo.content_type.startsWith('video/'));

const getMediaUrl = (photo) => {
  if (photo.file_url) {
    return photo.file_url.startsWith('http') ? photo.file_url : `${BACKEND_URL}${photo.file_url}`;
  }
  return photo.base64_data;
};

// Photo/Video Lightbox — supports both images and video playback + editing
export function PhotoLightbox({ photos, initialIndex = 0, onClose, onDelete, canDelete = false, onUpdate, events = [], albums = [] }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [editing, setEditing] = useState(false);
  const [editCaption, setEditCaption] = useState('');
  const [editEventId, setEditEventId] = useState('');
  const [editAlbumId, setEditAlbumId] = useState('');
  const [saving, setSaving] = useState(false);
  const currentPhoto = photos[currentIndex];

  const hasUnsavedChanges = () => {
    if (!editing) return false;
    return (
      editCaption !== (currentPhoto.caption || '') ||
      editEventId !== (currentPhoto.event_id || '') ||
      editAlbumId !== (currentPhoto.album_id || '')
    );
  };

  const confirmDiscard = () =>
    !hasUnsavedChanges() ||
    window.confirm('You have unsaved changes to this photo. Discard them?');

  const safeClose = () => { if (confirmDiscard()) onClose(); };

  const goToPrevious = useCallback(() => {
    if (editing && (
      editCaption !== (currentPhoto.caption || '') ||
      editEventId !== (currentPhoto.event_id || '') ||
      editAlbumId !== (currentPhoto.album_id || '')
    ) && !window.confirm('You have unsaved changes to this photo. Discard them?')) return;
    setCurrentIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
    setEditing(false);
  }, [photos.length, editing, editCaption, editEventId, editAlbumId, currentPhoto]);

  const goToNext = useCallback(() => {
    if (editing && (
      editCaption !== (currentPhoto.caption || '') ||
      editEventId !== (currentPhoto.event_id || '') ||
      editAlbumId !== (currentPhoto.album_id || '')
    ) && !window.confirm('You have unsaved changes to this photo. Discard them?')) return;
    setCurrentIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
    setEditing(false);
  }, [photos.length, editing, editCaption, editEventId, editAlbumId, currentPhoto]);

  const cancelEdit = () => {
    if (confirmDiscard()) setEditing(false);
  };

  const startEdit = () => {
    setEditCaption(currentPhoto.caption || '');
    setEditEventId(currentPhoto.event_id || '');
    setEditAlbumId(currentPhoto.album_id || '');
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('session_token');
      const body = {};
      if (editCaption !== (currentPhoto.caption || '')) body.caption = editCaption;
      if (editEventId !== (currentPhoto.event_id || '')) body.event_id = editEventId;
      if (editAlbumId !== (currentPhoto.album_id || '')) body.album_id = editAlbumId;

      if (Object.keys(body).length === 0) {
        setEditing(false);
        setSaving(false);
        return;
      }

      const res = await fetch(`${BACKEND_URL}/api/photos/${currentPhoto.photo_id}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to update');
      }

      toast.success('Photo details updated!');
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = originalStyle; };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't hijack keys when the user is typing in a form field — otherwise
      // arrow keys move between photos, Enter triggers nearby buttons, and
      // unsaved edits get wiped out.
      const t = e.target;
      const tag = (t && t.tagName) ? t.tagName.toUpperCase() : '';
      const isFormField = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable);
      if (isFormField) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        if (editing && (
          editCaption !== (currentPhoto.caption || '') ||
          editEventId !== (currentPhoto.event_id || '') ||
          editAlbumId !== (currentPhoto.album_id || '')
        )) {
          if (!window.confirm('You have unsaved changes to this photo. Discard them?')) return;
        }
        onClose();
      }
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [onClose, goToPrevious, goToNext, editing, editCaption, editEventId, editAlbumId, currentPhoto]);

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/photos/${currentPhoto.photo_id}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `media_${currentPhoto.photo_id}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download. Please try again.');
    }
  };

  // Save to Photos uses the Web Share API. On iOS Safari 15+, the share sheet
  // includes "Save Image" which puts the photo straight into the Photos app.
  // On Android Chrome, the same flow works via Google Photos / device gallery.
  // Falls back to a regular download if the browser doesn't support sharing files.
  const canShareFiles = typeof navigator !== 'undefined' && !!navigator.canShare;

  const handleSaveToPhotos = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/photos/${currentPhoto.photo_id}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Fetch failed');
      const blob = await response.blob();

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `legacy_forge_${currentPhoto.photo_id}.jpg`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }

      const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: filename });
        toast.success('Tap "Save Image" in the share menu to add to Photos');
      } else {
        // Fallback: trigger normal download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.info('Downloaded — open the file to save to your Photos app');
      }
    } catch (error) {
      // User cancelled the share sheet — not an error
      if (error?.name === 'AbortError') return;
      console.error('Save to photos error:', error);
      toast.error('Could not save photo. Please try Download instead.');
    }
  };

  const lightboxContent = (
    <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 99999 }} data-testid="photo-lightbox">
      <div className="absolute inset-0 bg-black/95" onClick={safeClose} data-testid="lightbox-backdrop" />

      <button onClick={safeClose} className="fixed top-4 left-4 flex items-center space-x-2 px-6 py-3 bg-white text-black rounded-lg hover:bg-gray-200 shadow-2xl font-bold text-lg border-2 border-gray-300" style={{ zIndex: 100000 }} data-testid="lightbox-close">
        <FaTimes className="text-xl" /><span>Close</span>
      </button>

      <div className="fixed top-4 right-4 text-white bg-black/60 px-4 py-2 rounded-lg text-sm" style={{ zIndex: 100000 }}>
        Press ESC or click dark area to close
      </div>

      {photos.length > 1 && (
        <>
          <button onClick={goToPrevious} className="fixed left-4 top-1/2 -translate-y-1/2 text-white text-5xl hover:text-gray-300 bg-black/50 p-3 rounded-full" style={{ zIndex: 100000 }} data-testid="lightbox-prev"><FaChevronLeft /></button>
          <button onClick={goToNext} className="fixed right-4 top-1/2 -translate-y-1/2 text-white text-5xl hover:text-gray-300 bg-black/50 p-3 rounded-full" style={{ zIndex: 100000 }} data-testid="lightbox-next"><FaChevronRight /></button>
        </>
      )}

      <div className="relative max-w-5xl max-h-[85vh] flex flex-col items-center px-16 overflow-y-auto" style={{ zIndex: 99999 }}>
        {isVideo(currentPhoto) ? (
          <video
            key={currentPhoto.photo_id}
            src={getMediaUrl(currentPhoto)}
            controls
            autoPlay
            className={`${editing ? 'max-h-[35vh]' : 'max-h-[65vh]'} max-w-full rounded-lg shadow-2xl bg-black transition-all`}
            data-testid="lightbox-video"
          />
        ) : (
          <img
            src={getMediaUrl(currentPhoto)}
            alt={currentPhoto.caption || 'Photo'}
            className={`${editing ? 'max-h-[35vh]' : 'max-h-[65vh]'} max-w-full object-contain rounded-lg shadow-2xl transition-all`}
            data-testid="lightbox-image"
          />
        )}

        <div className="mt-4 text-center text-white">
          <p className="text-sm text-gray-400">{currentIndex + 1} / {photos.length}</p>
          {(currentPhoto.event_name || currentPhoto.album_name) && !editing && (
            <p className="mt-1 text-sm text-blue-300">
              {currentPhoto.event_name && <><FaCalendarAlt className="inline mr-1 text-xs" />{currentPhoto.event_name}</>}
              {currentPhoto.album_name && !currentPhoto.event_name && <>{currentPhoto.album_name}</>}
            </p>
          )}
          {!editing && currentPhoto.caption && <p className="mt-2 text-lg">{currentPhoto.caption}</p>}
          {!editing && !currentPhoto.caption && <p className="mt-2 text-sm text-gray-500 italic">No caption — tap Edit to add one</p>}
          {!editing && <p className="text-sm text-gray-400 mt-1">Uploaded by {currentPhoto.uploader_name}</p>}

          {/* Edit Form */}
          {editing && (
            <div className="mt-3 bg-gray-800/90 rounded-xl p-4 text-left max-w-md mx-auto" data-testid="photo-edit-form">
              <div className="mb-3">
                <label className="block text-xs text-gray-400 mb-1">Caption / Description</label>
                <textarea
                  value={editCaption}
                  onChange={(e) => setEditCaption(e.target.value)}
                  placeholder="Who's in this photo? What's happening? Where was it taken?"
                  rows={3}
                  className="w-full bg-gray-900 border border-gray-600 rounded-lg p-2.5 text-white text-sm placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  data-testid="photo-edit-caption"
                />
              </div>
              {events.length > 0 && (
                <div className="mb-3">
                  <label className="block text-xs text-gray-400 mb-1">Event</label>
                  <select
                    value={editEventId}
                    onChange={(e) => setEditEventId(e.target.value)}
                    className="w-full bg-gray-900 border border-gray-600 rounded-lg p-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    data-testid="photo-edit-event"
                  >
                    <option value="">No event</option>
                    {events.map(ev => (
                      <option key={ev.event_id} value={ev.event_id}>{ev.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {albums.length > 0 && (
                <div className="mb-3">
                  <label className="block text-xs text-gray-400 mb-1">Album</label>
                  <select
                    value={editAlbumId}
                    onChange={(e) => setEditAlbumId(e.target.value)}
                    className="w-full bg-gray-900 border border-gray-600 rounded-lg p-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    data-testid="photo-edit-album"
                  >
                    <option value="">No album</option>
                    {albums.map(al => (
                      <option key={al.album_id} value={al.album_id}>{al.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex gap-2 justify-end">
                <button
                  onClick={cancelEdit}
                  className="px-3 py-1.5 text-sm text-gray-300 hover:text-white transition"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
                  data-testid="photo-edit-save"
                >
                  <FaSave className="text-xs" />
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2 sm:gap-4">
          {!editing && (
            <button onClick={startEdit} className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm sm:text-base" data-testid="lightbox-edit">
              <FaEdit /><span>Edit Details</span>
            </button>
          )}
          {canShareFiles && !isVideo(currentPhoto) && (
            <button onClick={handleSaveToPhotos} className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm sm:text-base" data-testid="lightbox-save-to-photos">
              <FaImage /><span>Save to Photos</span>
            </button>
          )}
          <button onClick={handleDownload} className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 text-sm sm:text-base" data-testid="lightbox-download">
            <FaDownload /><span>Download</span>
          </button>
          {canDelete && onDelete && (
            <button onClick={() => onDelete(currentPhoto.photo_id)} className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm sm:text-base" data-testid="lightbox-delete">
              <FaTrash /><span>Delete</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(lightboxContent, document.body);
}


// Media Grid — shows image thumbnails and video thumbnails with play overlay
export function PhotoGrid({ photos, onPhotoClick, onDelete, canDelete = false, emptyMessage = 'No photos yet' }) {
  if (photos.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-md p-12 text-center">
        <p className="text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="photo-grid">
      {photos.map((photo, index) => (
        <MediaThumbnail
          key={photo.photo_id}
          photo={photo}
          onClick={() => onPhotoClick(index)}
          onDelete={canDelete && onDelete ? () => onDelete(photo.photo_id) : null}
        />
      ))}
    </div>
  );
}


// Reusable thumbnail component — used by grid and timeline views
export function MediaThumbnail({ photo, onClick, onDelete, className = '' }) {
  const videoItem = isVideo(photo);

  return (
    <div
      className={`relative group cursor-pointer rounded-xl overflow-hidden shadow-md aspect-square ${className}`}
      onClick={onClick}
      data-testid={`photo-item-${photo.photo_id}`}
    >
      {videoItem ? (
        <>
          {/* Video poster — use a dark bg with play icon */}
          <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
            <video
              src={getMediaUrl(photo)}
              className="w-full h-full object-cover"
              preload="metadata"
              muted
            />
          </div>
          {/* Play overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition">
            <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <FaPlay className="text-gray-800 text-lg ml-1" />
            </div>
          </div>
          {/* Video badge */}
          <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
            <FaVideo size={10} /> Video
          </div>
        </>
      ) : (
        <img
          src={getMediaUrl(photo)}
          alt={photo.caption || 'Photo'}
          className="w-full h-full object-cover transition-transform group-hover:scale-105"
          loading="lazy"
        />
      )}

      {/* Caption overlay on hover */}
      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-end pointer-events-none">
        <div className="w-full p-3 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
          <p className="text-white text-sm truncate">{photo.caption || 'No caption'}</p>
          <p className="text-gray-300 text-xs">{photo.uploader_name}</p>
        </div>
      </div>

      {/* Delete button */}
      {onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
          data-testid={`delete-photo-${photo.photo_id}`}
        >
          <FaTrash className="text-sm" />
        </button>
      )}
    </div>
  );
}


// Upload component — accepts images and videos with validation and progress.
// Supports multi-file upload: when the user selects/drops multiple files, they
// are uploaded sequentially (so the parent's onUpload can stay simple) and
// progress shows "Uploading X of Y…" as a friendly overall counter.
export function PhotoUpload({ onUpload, uploading = false, uploadProgress = 0, accept = 'image/*,.heic,.heif,video/mp4,video/quicktime,video/webm', maxSize = 200 }) {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);
  const [validating, setValidating] = useState(false);
  const [fileInfo, setFileInfo] = useState(null);
  const [batchProgress, setBatchProgress] = useState(null); // { current, total }

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  /**
   * Pre-flight check: macOS Photos app + iCloud "Optimize Mac Storage" deliver a
   * placeholder file to Safari when dragged. The File object reports a size but
   * reading the bytes fails or returns empty. This check tries to read the first
   * 64KB and verifies we actually got bytes; if not, we surface a clear error
   * instead of letting the upload silently fail later.
   */
  const verifyFileReadable = (file) => new Promise((resolve) => {
    if (!file || file.size === 0) {
      resolve({ ok: false, reason: 'empty' });
      return;
    }
    const slice = file.slice(0, Math.min(64 * 1024, file.size));
    const reader = new FileReader();
    reader.onload = () => {
      const len = reader.result?.byteLength ?? 0;
      if (len === 0) resolve({ ok: false, reason: 'unreadable' });
      else resolve({ ok: true });
    };
    reader.onerror = () => resolve({ ok: false, reason: 'unreadable' });
    reader.onabort = () => resolve({ ok: false, reason: 'aborted' });
    try {
      reader.readAsArrayBuffer(slice);
    } catch {
      resolve({ ok: false, reason: 'unreadable' });
    }
  });

  const validateAndUpload = async (rawFile) => {
    setError(null);
    setFileInfo(null);
    setValidating(true);

    try {
      // 0) Pre-flight: confirm we can actually read this file. macOS Photos drag
      // gives Safari a stub when iCloud has only the cloud copy.
      const readable = await verifyFileReadable(rawFile);
      if (!readable.ok) {
        setValidating(false);
        setError(
          "Safari couldn't read this file. If you dragged from the macOS Photos app, " +
          "the photo is stored in iCloud and only a preview was given to the browser. " +
          "Click \"Choose File\" below and pick the photo, or first drag it to your Desktop, " +
          "then drag from there."
        );
        return;
      }

      const { validateMediaFile, isHeicFile, convertHeicToJpeg, compressImage } = await import('../utils/chunkedUpload');

      // Auto-convert HEIC/HEIF from iPhones to JPEG before validation/upload.
      let file = rawFile;
      if (isHeicFile(rawFile)) {
        setFileInfo({ name: rawFile.name, sizeMB: (rawFile.size / 1024 / 1024).toFixed(1), converting: true });
        file = await convertHeicToJpeg(rawFile);
      }

      // Compress/downscale large photos in the browser so big iPhone shots
      // upload reliably and fast. Videos and already-small images are untouched.
      if (!file.type.startsWith('video/') && file.size > 3 * 1024 * 1024) {
        setFileInfo({ name: file.name, sizeMB: (file.size / 1024 / 1024).toFixed(1), compressing: true });
        file = await compressImage(file);
      }

      const result = await validateMediaFile(file);

      if (!result.valid) {
        setError(result.error);
        setValidating(false);
        return;
      }

      const isVid = file.type.startsWith('video/');
      const info = { name: file.name, sizeMB: result.sizeMB.toFixed(1), isVideo: isVid };
      if (result.duration) {
        const mins = Math.floor(result.duration / 60);
        const secs = Math.floor(result.duration % 60);
        info.duration = `${mins}:${secs.toString().padStart(2, '0')}`;
      }
      setFileInfo(info);
      setValidating(false);

      await onUpload(file);
    } catch {
      setError('Could not validate file. Please try again.');
      setValidating(false);
    }
  };

  // Bulk upload entry point — iterates files sequentially so each gets HEIC
  // conversion, readability checks, and the parent's progress UI.
  const validateAndUploadMany = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    if (files.length === 1) {
      await validateAndUpload(files[0]);
      return;
    }
    setError(null);
    setBatchProgress({ current: 0, total: files.length, failed: 0 });
    let failed = 0;
    for (let i = 0; i < files.length; i++) {
      setBatchProgress({ current: i + 1, total: files.length, failed });
      try {
        await validateAndUpload(files[i]);
      } catch (e) {
        console.error('Upload of file failed:', files[i]?.name, e);
        failed += 1;
      }
    }
    setBatchProgress(null);
    if (failed > 0) {
      setError(`${failed} of ${files.length} files failed to upload. The rest succeeded.`);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      validateAndUploadMany(files);
    }
  };

  const handleChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      validateAndUploadMany(files);
    }
    // Reset so re-selecting the same files still triggers onChange
    e.target.value = '';
  };

  const isActive = uploading || validating;

  return (
    <div className="mb-6">
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
          dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        data-testid="photo-upload-zone"
      >
        <input
          type="file"
          accept={accept}
          onChange={handleChange}
          multiple
          className="hidden"
          id="photo-upload-input"
          disabled={isActive}
          data-testid="photo-upload-input"
        />
        <label htmlFor="photo-upload-input" className="cursor-pointer">
          {validating ? (
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-3"></div>
              <p className="text-gray-600">
                {fileInfo?.converting
                  ? 'Converting iPhone photo (HEIC → JPEG)…'
                  : fileInfo?.compressing
                  ? 'Optimizing photo for upload…'
                  : 'Checking file...'}
              </p>
              {batchProgress && (
                <p className="text-sm font-medium text-blue-700 mt-1">
                  File {batchProgress.current} of {batchProgress.total}
                  {batchProgress.failed > 0 ? ` (${batchProgress.failed} failed)` : ''}
                </p>
              )}
              {(fileInfo?.converting || fileInfo?.compressing) && (
                <p className="text-xs text-gray-500 mt-1">This may take a moment for large photos.</p>
              )}
            </div>
          ) : uploading ? (
            <div className="flex flex-col items-center">
              <div className="w-full max-w-xs mb-3">
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>
                    {batchProgress
                      ? `Uploading ${batchProgress.current} of ${batchProgress.total}…`
                      : `Uploading${fileInfo ? ` ${fileInfo.name}` : ''}...`}
                  </span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-blue-600 h-3 rounded-full transition-all"
                    style={{ width: `${uploadProgress}%` }}
                    data-testid="upload-progress-bar"
                  />
                </div>
                {fileInfo && (
                  <p className="text-xs text-gray-500 mt-1">
                    {fileInfo.sizeMB}MB{fileInfo.duration ? ` | ${fileInfo.duration}` : ''}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-600 mb-1">
                <span className="text-blue-600 font-medium">Click to upload</span> or drag and drop
              </p>
              <p className="text-gray-400 text-sm">Pick multiple photos at once! PNG, JPG, HEIC up to 25MB each — videos (MP4, MOV, WebM) up to 200MB, max 5 min.</p>
              <p className="text-gray-400 text-xs mt-2">
                Mac tip: drag from <strong>Finder</strong> or click "Click to upload" — dragging directly from the Photos app may not work if iCloud has only the cloud copy.
              </p>
            </div>
          )}
        </label>
      </div>
      {error && <p className="mt-2 text-red-600 text-sm" data-testid="upload-error">{error}</p>}
    </div>
  );
}

export default PhotoLightbox;
